import { formatDurationRangeLabel } from "@clipboard-health/date-time";
import { TextSkeleton, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, useTheme } from "@mui/material";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { ScoreCounter } from "@src/appV2/redesign/EmergencyCredits/components/ScoreCounter";
import { ScoreLineItem } from "@src/appV2/redesign/EmergencyCredits/components/ScoreLineItem";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { useCallback, useEffect, useState } from "react";

import { CancellationCreditExplanationModal } from "./components/CancellationCreditExplanationModal";
import { ShiftCancelFooter } from "./components/ShiftCancelFooter";
import { ShiftCancelHeader } from "./components/ShiftCancelHeader";
import { useCancellationScoreAnimation } from "./hooks/useCancellationScoreAnimation";
import type { ShiftCancelPageData } from "./hooks/useShiftCancelPageData";
import { getCreditExplanationVariant } from "./utils/getCreditExplanationVariant";
import { getScoreColor } from "./utils/getScoreColor";

export interface ShiftCancelPageContentHandlers {
  isCancelling: boolean;
  onKeepShift: () => void;
  onPressAndHoldToCancel: () => void;
}

export type ShiftCancelPageContentProps = ShiftCancelPageData & ShiftCancelPageContentHandlers;

function ShiftCancelPageContentSkeleton(): JSX.Element {
  return (
    <>
      <ShiftCancelHeader />
      <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
        <TextSkeleton variant="h2" width="60%" />
        <TextSkeleton variant="body2" width="80%" />
        <TextSkeleton variant="h1" width="30%" />
        <TextSkeleton variant="body2" width="100%" />
        <TextSkeleton variant="body2" width="100%" />
      </Stack>
    </>
  );
}

export function ShiftCancelPageContent(props: ShiftCancelPageContentProps): JSX.Element | null {
  const theme = useTheme();
  const creditExplainerModalState = useModalState();
  const [isCreditPreviewed, setIsCreditPreviewed] = useState(false);
  const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);

  // Automatically apply the credit for workers who are about to become restricted anyway.
  const isDataReady = props.enabled && !props.isLoading && !props.isError;
  const shouldAutoApplyCredit = isDataReady && props.isBecomingRestricted && props.canUseCredit;
  useEffect(() => {
    if (shouldAutoApplyCredit) {
      setIsCreditPreviewed(true);
    }
  }, [shouldAutoApplyCredit]);

  const animation = useCancellationScoreAnimation({
    currentScore: isDataReady ? props.currentScore : 0,
    penalizedScore: isDataReady ? props.newScoreBeforeCredit : 0,
    finalScore: isDataReady ? props.finalScore : 0,
    canUseCredit: isDataReady ? props.canUseCredit : false,
    shouldAutoApplyCredit,
  });

  const { startCreditAnimation } = animation;
  const handlePreviewCredit = useCallback(() => {
    startCreditAnimation();
    setIsCreditPreviewed(true);
    setIsPreviewAnimating(true);
  }, [startCreditAnimation]);

  const handlePreviewAnimationComplete = useCallback(() => {
    setIsPreviewAnimating(false);
  }, []);

  if (!props.enabled || props.isLoading) {
    return <ShiftCancelPageContentSkeleton />;
  }

  if (props.isError) {
    return (
      <Stack
        sx={{
          position: "relative",
          flex: 1,
          marginTop: getSafeAreaPaddings({ top: true }).paddingTop,
        }}
      >
        <TopLeftBackButton includeTopSpacing={false} onClick={props.onKeepShift} />
        <ShiftCancelHeader />
        <Alert severity="error" sx={{ margin: 4 }}>
          Something went wrong while loading cancellation details. Please try again.
        </Alert>
      </Stack>
    );
  }

  const {
    isWorkerRestricted,
    cancellationPolicy,
    currentScore,
    baseScoreChange,
    creditBenefit,
    initialECStats,
    finalECStats,
    hasCancellationCredit,
    isWithinECWindow,
    isAfterShiftStart,
    canUseCredit,
    isCancelling,
    onKeepShift,
    onPressAndHoldToCancel,
  } = props;

  const creditExplanationVariant = getCreditExplanationVariant({
    isAfterShiftStart,
    isWithinECWindow,
    creditBenefit,
  });

  const isZeroPointsCancellation = !isWorkerRestricted && baseScoreChange === 0;
  const isPendingPreview =
    !isWorkerRestricted && !isZeroPointsCancellation && canUseCredit && !isCreditPreviewed;

  const showScoreSection =
    !isWorkerRestricted &&
    !isZeroPointsCancellation &&
    isDefined(initialECStats) &&
    isDefined(finalECStats);

  const [from, to] = cancellationPolicy?.leadTime ?? [0, undefined];
  const policyLabel =
    (from ?? 0) === 0 && to === 0
      ? "Cancel after shift start"
      : `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
          format: "abbreviated",
          keepOriginalUnit: true,
        })} cancellation`;

  const showWhyCantIUseCredit =
    !isWorkerRestricted && !isZeroPointsCancellation && !canUseCredit && hasCancellationCredit;

  const isAnyAnimationInProgress =
    showScoreSection && (!animation.isAnimationComplete || isPreviewAnimating);

  const scoreColor = getScoreColor(animation.scoreVariant, theme);

  return (
    <Stack
      sx={{
        position: "relative",
        flex: 1,
        marginTop: getSafeAreaPaddings({ top: true }).paddingTop,
      }}
    >
      {!isCancelling && <TopLeftBackButton includeTopSpacing={false} onClick={onKeepShift} />}
      <ShiftCancelHeader
        isWorkerRestricted={isWorkerRestricted}
        isZeroPointsCancellation={isZeroPointsCancellation}
        verticallyCenter={!showScoreSection}
      />

      {showScoreSection && (
        <Stack sx={{ flex: 1, paddingY: 8 }}>
          <Stack alignItems="center" justifyContent="center" sx={{ paddingY: 4 }}>
            <ScoreCounter
              startValue={animation.previousScore}
              endValue={animation.displayScore}
              animationEnabled={animation.isScoreAnimating}
              color={scoreColor}
              duration={animation.scoreDuration}
            />
          </Stack>

          {/* Only render progress bar for <24h cancellations */}
          {isWithinECWindow && (
            <Stack sx={{ paddingX: 6, paddingY: 4 }}>
              <CancellationCreditProgressBar
                showShiftsProgress={!hasCancellationCredit}
                cancellationCreditStats={isCreditPreviewed ? finalECStats : initialECStats}
                mode={isCreditPreviewed ? "drain" : "static"}
                label={isCreditPreviewed ? "Applying credit..." : undefined}
                onAnimationComplete={handlePreviewAnimationComplete}
              />
            </Stack>
          )}

          <Stack spacing={4} sx={{ paddingX: 6, paddingY: 4 }}>
            <ScoreLineItem
              isBaseline
              isVisible
              label="Current Clipboard Score"
              value={currentScore}
            />
            {baseScoreChange !== 0 && (
              <ScoreLineItem
                label={policyLabel}
                value={baseScoreChange}
                color="negative"
                isVisible={animation.isPenaltyLineItemVisible}
              />
            )}
            {isCreditPreviewed && creditBenefit > 0 && (
              <ScoreLineItem
                label="Same-day cancellation credit"
                value={creditBenefit}
                color="positive"
                isVisible={animation.isCreditLineItemVisible}
                icon={
                  <ShieldIcon
                    variant="outlined"
                    sx={(theme) => ({
                      color: theme.palette.success.main,
                      width: 16,
                      height: 16,
                    })}
                  />
                }
                iconPosition="right"
              />
            )}
          </Stack>
        </Stack>
      )}

      <ShiftCancelFooter
        variant={isPendingPreview ? "preview-credit" : "cancel-shift"}
        showWhyCantIUseCredit={showWhyCantIUseCredit}
        isCancelling={isCancelling}
        isPreviewAnimating={isAnyAnimationInProgress}
        onKeepShift={onKeepShift}
        onPressAndHoldToCancel={onPressAndHoldToCancel}
        onPreviewCredit={handlePreviewCredit}
        onWhyCantIUseCredit={creditExplainerModalState.openModal}
      />

      <CancellationCreditExplanationModal
        variant={creditExplanationVariant}
        modalState={creditExplainerModalState}
      />
    </Stack>
  );
}
